import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useOutletContext } from 'react-router-dom';
import { createUserWithEmailAndPassword } from 'firebase/auth';
import { auth, signInWithGoogle } from '../../../utils/firebase';
import Typography from '@mui/material/Typography';
import Styles from '../../../styles/Forms.module.css'
import InputLabel from '@mui/material/InputLabel';
import FormControl from '@mui/material/FormControl';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import GoogleLogo from '../../../images/google_logo.webp'
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import OutlinedInput from '@mui/material/OutlinedInput';
import InputAdornment from '@mui/material/InputAdornment';
import FormHelperText from '@mui/material/FormHelperText';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import CloseIcon from '@mui/icons-material/Close';
import CheckIcon from '@mui/icons-material/Check';
import { isValidText, isValidEmail } from '../../../utils/strings';
import FormSide from '../../../images/form_side.webp'
import EasySnackBar from '../../../components/EasySnackBar';
import EmailIcon from '@mui/icons-material/Email';
import Stack from '@mui/material/Stack';

import logo from '../../../images/big_logo.webp'

export default function Signup() {
    const navigate = useNavigate()
    const [userAuth, userData, userMenu, updateUserData, updateUserMenu, setPreventNavigation] = useOutletContext()

    const [signupWithEmail, setSignupWithEmail] = useState(false)
    const [email, setEmail] = useState('')
    const [showPassword, setShowPassword] = useState(false);
    const [password, setPassword] = useState('');
    const [repeatedPassword, setRepeatedPassword] = useState('')

    const [error, setError] = useState({ length: true, validText: true, repeated: true, validEmail: true })

    const [errorMessage, setErrorMessage] = useState('')

    useEffect(() => {
        if (userAuth) {
            navigate("/admin/app")
        }
    }, [userAuth, navigate])

    useEffect(() => {
        setError(
            prev => {
                return ({
                    length: password.length < 8,
                    validText: !isValidText(password),
                    repeated: password !== repeatedPassword,
                    validEmail: prev.validEmail
                })
            }
        )
    }, [password, repeatedPassword])

    useEffect(() => {
        setError(
            prev => {
                return ({
                    length: prev.length,
                    validText: prev.validText,
                    repeated: prev.repeated,
                    validEmail: !isValidEmail(email)
                })
            }
        )
    }, [email])

    const onSubmit = useCallback(async (e) => {
        e.preventDefault()

        if (!error.length && !error.validText && !error.repeated) {
            await createUserWithEmailAndPassword(auth, email, password)
                .then((userCredential) => {
                    navigate("/admin/app")
                })
                .catch((error) => {
                    setErrorMessage('Algo salió mal, intenta de nuevo mas tarde')
                });
        } else {
            setErrorMessage('Por favor, revisa que los datos ingresados sean validos')
        }
    }, [error, auth, email, password, navigate])

    return (
        <>
            {!signupWithEmail ?
                <div className={Styles.container}>
                    <Stack
                        direction='row'
                        flexWrap='wrap'
                        justifyContent='center'
                        className={Styles.content}
                    >
                        <Stack spacing={2} className={Styles.form}>
                            <img src={logo} height={75} width={"auto"} style={{ margin: "auto", cursor: "pointer" }} onClick={() => navigate('/')} />

                            <span className={Styles.title}>
                                ¡Hola! Estás a punto de unirte a Tookeat
                            </span>

                            <Divider />

                            <TextField
                                label="Email"
                                variant="outlined"
                                value={email}
                                onChange={e => setEmail(e.target.value)}
                                required
                                type='email'
                                error={error.validEmail && email !== ''}
                                onKeyDown={(e) => {
                                    if (e.key === "Enter") {
                                        setSignupWithEmail(true)
                                    }
                                }}
                            />

                            <Button
                                onClick={() => setSignupWithEmail(true)}
                                variant='outlined' startIcon={<EmailIcon />}>
                                Registrarse con email
                            </Button>

                            <Divider>Tambien puedes</Divider>

                            <Button variant="outlined" onClick={async () => signInWithGoogle()}>
                                <img src={GoogleLogo} className={Styles.loginMethodLogo} alt='google logo' />
                                Registrarse con Google
                            </Button>

                            <Divider />

                            <p>
                                Ya tienes una cuenta? <span
                                    className={Styles.actionText}
                                    onClick={() => navigate('/admin/login')}>
                                    Ingresa
                                </span>
                            </p>

                        </Stack>
                        <div className={Styles.sideImage} style={{ backgroundImage: 'url(' + FormSide + ')' }}></div>
                    </Stack>
                </div>
                :
                <div className={Styles.container}>
                    <Stack
                        direction='row'
                        flexWrap='wrap'
                        justifyContent='center'
                        className={Styles.content}
                    >
                        <Stack spacing={2} className={Styles.form}>
                            <img src={logo} onClick={() => navigate('/')} className={Styles.logo} />
                            <span className={Styles.title}>
                                Crear una cuenta
                            </span>

                            <TextField
                                label="Email"
                                variant="outlined"
                                value={email}
                                onChange={e => setEmail(e.target.value)}
                                required
                                type='email'
                                error={error.validEmail && email !== ''}
                            />

                            <FormControl required variant="outlined">
                                <InputLabel htmlFor="outlined-adornment-password">Contraseña</InputLabel>
                                <OutlinedInput
                                    id="outlined-adornment-password"
                                    value={password}
                                    onChange={e => setPassword(e.target.value)}

                                    required
                                    type={showPassword ? 'text' : 'password'}
                                    endAdornment={
                                        <InputAdornment position="end">
                                            <IconButton
                                                aria-label="toggle password visibility"
                                                onClick={setShowPassword.bind(this, prev => !prev)}
                                                onMouseDown={e => e.preventDefault()}
                                                edge="end"
                                            >
                                                {showPassword ? <VisibilityOff /> : <Visibility />}
                                            </IconButton>
                                        </InputAdornment>
                                    }
                                    label="Contraseña"
                                />
                            </FormControl>

                            <FormControl required variant="outlined">
                                <InputLabel htmlFor="outlined-adornment-passwordd">Repetir contraseña</InputLabel>
                                <OutlinedInput
                                    id="outlined-adornment-passwordd"
                                    value={repeatedPassword}
                                    onChange={e => setRepeatedPassword(e.target.value)}
                                    required
                                    type={showPassword ? 'text' : 'password'}
                                    endAdornment={
                                        <InputAdornment position="end">
                                            <IconButton
                                                aria-label="toggle password visibility"
                                                onClick={setShowPassword.bind(this, prev => !prev)}
                                                onMouseDown={e => e.preventDefault()}
                                                edge="end"
                                            >
                                                {showPassword ? <VisibilityOff /> : <Visibility />}
                                            </IconButton>
                                        </InputAdornment>
                                    }
                                    label="Repetir contraseña"
                                />
                            </FormControl>

                            <div>
                                <Stack direction='row' sx={{ m: 1 }}>
                                    {error.length ? <CloseIcon color='error' /> : <CheckIcon color='success' />}
                                    <span style={{ color: error.length ? '#D32F2F' : '#2E7D32' }}> La contraseña debe tener al menos 8 caracteres</span>
                                </Stack>

                                <Stack direction='row' sx={{ m: 1 }}>
                                    {error.validText ? <CloseIcon color='error' /> : <CheckIcon color='success' />}
                                    <span style={{ color: error.validText ? '#D32F2F' : '#2E7D32' }}> Solo puede incluir numeros, letras o simbolos</span>
                                </Stack>

                                <Stack direction='row' sx={{ m: 1 }}>
                                    {error.repeated ? <CloseIcon color='error' /> : <CheckIcon color='success' />}
                                    <span style={{ color: error.repeated ? '#D32F2F' : '#2E7D32' }}> Las contraseñas deben coincidir</span>
                                </Stack>
                            </div>

                            <FormControl disabled>
                                <InputLabel>País</InputLabel>
                                <Select value={0} label="País">
                                    <MenuItem value={0}>Argentina</MenuItem>
                                </Select>
                                <FormHelperText>Solo disponible en Argentina por el momento</FormHelperText>
                            </FormControl>

                            <Button variant="contained" onClick={onSubmit.bind(this)}                            >
                                Crear cuenta</Button>

                            <Divider>Tambien puedes</Divider>

                            <Button variant="outlined" onClick={async () => signInWithGoogle()}>
                                <img src={GoogleLogo} className={Styles.loginMethodLogo} alt='google logo' />
                                Registrarte con Google
                            </Button>

                            <p>
                                Ya tienes una cuenta? <span
                                    className={Styles.actionText}
                                    onClick={() => navigate('/admin/login')}>
                                    Ingresa
                                </span>
                            </p>

                        </Stack>
                        <div className={Styles.sideImage} style={{ backgroundImage: 'url(' + FormSide + ')' }}></div>
                    </Stack>
                </div>}

            <EasySnackBar
                severity='error'
                key='1'
                message={errorMessage}
                setMessage={setErrorMessage}
            />
        </>
    )
}

